import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setCredentials, setError, setLoading, selectAuthError, selectAuthLoading, selectCurrentUser, selectIsAuthenticated, selectCurrentUserRole } from '../../store/slices/authSlice';
import api from '../../api/axios';
import { showSuccess, showError } from '../../utils/toast';

// Common country codes
const countryCodes = [
    { code: '+91', country: 'India' }, 
];

// User types
const userTypes = [
    { value: 'patient', label: 'Patient' },
    { value: 'doctor', label: 'Doctor' },
    { value: 'clinic', label: 'Clinic' },
    // { value: 'hospital', label: 'Hospital' },
];

export default function Login() {
    const user = useSelector(selectCurrentUser);
    const isAuthenticated = useSelector(selectIsAuthenticated);
    const userRole = useSelector(selectCurrentUserRole);
    const dispatch = useDispatch();
    const error = useSelector(selectAuthError);
    const loading = useSelector(selectAuthLoading);
    const navigate = useNavigate();
    
    const [showOTP, setShowOTP] = useState(false);
    const [showOTPBtn, setShowOTPBtn] = useState(true);
    const [otpCountdown, setOtpCountdown] = useState(false);
    const [cooldownSeconds, setCountdownSeconds] = useState(60);
    const [userType, setUserType] = useState('patient'); // Default to patient
    const [showClinicRegister, setShowClinicRegister] = useState(false);
    const isClinicOrHospital = userType === 'clinic' || userType === 'hospital';
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [forgotStep, setForgotStep] = useState('email');
    const [forgotMessage, setForgotMessage] = useState('');
    const [resetSuccessMessage, setResetSuccessMessage] = useState('');

    const [formData, setFormData] = useState({
        email: '',
        password: '',
        mobile: '',
        otp: ''
    });
    const [registerData, setRegisterData] = useState({
        clinic_name: '',
        owner_name_prefix: '',
        owner_name: '',
        email: '',
        password: '',
        password_confirmation: '',
        contact_number_code: '+91',
        contact_number: ''
    });
    const [forgotData, setForgotData] = useState({
        email: '',
        otp: '',
        password: '',
        password_confirmation: ''
    });
    const [countryCode, setCountryCode] = useState('+91'); // Default to India

    useEffect(() => {
        document.title = 'Varosa - Login';
        // Check authentication status
        if (isAuthenticated) {
            if(user?.name === null){
                navigate(`/dashboard/${userRole}/profile/edit`);
            }else{
                navigate(`/dashboard/${userRole}`);
            }
            return;
        }
    }, [isAuthenticated, user, userRole, navigate]);

    useEffect(() => {
        let interval;
        if (otpCountdown && cooldownSeconds > 0) {
            interval = setInterval(() => {
                setCountdownSeconds(prev => prev - 1);
            }, 1000);
        } else if (cooldownSeconds === 0) {
            setShowOTPBtn(true);
            setOtpCountdown(false);
            setCountdownSeconds(60);
        }
        return () => clearInterval(interval);
    }, [otpCountdown, cooldownSeconds]);

    const handleSendOTP = async () => {
        if (!formData.mobile) {
            dispatch(setError('Please enter a valid mobile number'));
            return;
        }

        try {
            dispatch(setLoading(true));
            const response = await api.post(`/${userType}/send-otp`, {
                phone_code: countryCode,
                phone_number: formData.mobile,
                user_type: userType,
            });

            if (response?.data?.result === 'success') {
                setShowOTP(true);
                setShowOTPBtn(false);
                setOtpCountdown(true);
            }else if (response?.data?.result === 'failed') {
                dispatch(setError(response?.data?.message));
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        dispatch(setError(item));
                    });
                }
            }else{
                dispatch(setError('Failed to send OTP. Please try again later.'));
            }
            dispatch(setLoading(false));
        } catch (err) {
            dispatch(setLoading(false));
            dispatch(setError(err.response?.data?.message || 'Failed to send OTP. Please try again.'));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        dispatch(setLoading(true));
        try {
            const response = isClinicOrHospital
                ? await api.post(`/${userType}/login`, {
                    email: formData.email,
                    password: formData.password,
                })
                : await api.post(`/${userType}/verify-otp`, { 
                    phone_code: countryCode, 
                    phone_number: formData.mobile, 
                    otp: formData.otp 
                });

            if (response?.data?.result === 'success') {
                // Store the token if provided
                if (response?.data?.data?.access_token) {
                    setUserType(response?.data?.data?.role);
                    // We'll keep this for backward compatibility
                    localStorage.setItem('token', response?.data?.data?.access_token);

                    // Set default authorization header for future requests
                    api.defaults.headers.common['Authorization'] = `Bearer ${response?.data?.data?.access_token}`;

                    // Set credentials in Redux (which will also save to localStorage)
                    dispatch(setCredentials({
                        user: response?.data?.data?.user,
                        token: response?.data?.data?.access_token,
                        role: response?.data?.data?.role
                    }));
                }

                if(response?.data?.data?.user?.name === null){
                    navigate(`/dashboard/${userType}/profile/edit`);
                }else{
                    navigate(`/dashboard/${userType}`);
                }
            }else if (response?.data?.result === 'failed') {
                dispatch(setError(response?.data?.message));
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        dispatch(setError(item));
                    });
                }
            }else{
                dispatch(setError('Login failed. Please try again later.'));
            }
            dispatch(setLoading(false));
        } catch (err) {
            dispatch(setLoading(false));
            dispatch(setError(err.response?.data?.message || 'Login failed'));
        }
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleRegisterChange = (e) => {
        setRegisterData({
            ...registerData,
            [e.target.name]: e.target.value
        });
    };

    const handleForgotChange = (e) => {
        setForgotData({
            ...forgotData,
            [e.target.name]: e.target.value
        });
    };

    const resetForgotPasswordForm = () => {
        setForgotStep('email');
        setForgotMessage('');
        setForgotData({
            email: '',
            otp: '',
            password: '',
            password_confirmation: ''
        });
    };

    const openForgotPassword = () => {
        dispatch(setError(null));
        setForgotData({
            email: formData.email,
            otp: '',
            password: '',
            password_confirmation: ''
        });
        setForgotStep('email');
        setForgotMessage('');
        setShowForgotPassword(true);
    };

    const closeForgotPassword = () => {
        setShowForgotPassword(false);
        resetForgotPasswordForm();
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        dispatch(setLoading(true));
        setForgotMessage('');
        setResetSuccessMessage('');
        try {
            const response = await api.post(`/${userType}/forgot-password`, {
                email: forgotData.email,
            });
            if (response?.data?.result === 'success') {
                setForgotStep('reset');
                setForgotMessage(response?.data?.message || 'OTP sent to your email successfully');
            } else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response.data.errors.forEach((item) => showError(item));
                }
            } else {
                showError('Failed to send OTP. Please try again later.');
            }
        } catch (err) {
            showError(err.response?.data?.message || 'Failed to send OTP. Please try again.');
        } finally {
            dispatch(setLoading(false));
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        dispatch(setLoading(true));
        setForgotMessage('');
        try {
            const response = await api.post(`/${userType}/reset-password`, forgotData);
            if (response?.data?.result === 'success') {
                setFormData({
                    ...formData,
                    email: forgotData.email,
                    password: ''
                });
                setResetSuccessMessage(response?.data?.message || 'Password reset successfully. Please login with your new password.');
                closeForgotPassword();
                dispatch(setError(null));
            } else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response.data.errors.forEach((item) => showError(item));
                }
            } else {
                showError('Password reset failed. Please try again later.');
            }
        } catch (err) {
            showError(err.response?.data?.message || 'Password reset failed');
        } finally {
            dispatch(setLoading(false));
        }
    };

    const handleClinicRegister = async (e) => {
        e.preventDefault();
        dispatch(setLoading(true));
        try {
            const response = await api.post(`/${userType}/register`, registerData);
            if (response?.data?.result === 'success') {
                setShowClinicRegister(false);
                if (response?.data?.data?.access_token) {
                    localStorage.setItem('token', response.data.data.access_token);
                    api.defaults.headers.common['Authorization'] = `Bearer ${response.data.data.access_token}`;
                    dispatch(setCredentials({
                        user: response.data.data.user,
                        token: response.data.data.access_token,
                        role: response.data.data.role
                    }));
                    navigate(`/dashboard/${userType}/profile/edit`);
                }
            } else if (response?.data?.result === 'failed') {
                dispatch(setError(response?.data?.message));
                if(response?.data?.errors){
                    response.data.errors.forEach((item) => dispatch(setError(item)));
                }
            } else {
                dispatch(setError('Registration failed. Please try again later.'));
            }
        } catch (err) {
            dispatch(setError(err.response?.data?.message || 'Registration failed'));
        } finally {
            dispatch(setLoading(false));
        }
    };

    // Auto-dismiss error alert after 3 seconds
    useEffect(() => {
        let timeoutId;
        if (error) {
            timeoutId = setTimeout(() => {
                dispatch(setError(null));
            }, 3000);
        }
        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [error, dispatch]);

    return (
        <>
            {loading && (
                <div id="spinner" className="bg-white position-fixed translate-middle w-100 vh-100 top-50 start-50 d-flex align-items-center justify-content-center" style={{ opacity: 0.5, visibility: 'visible'}}>
                    <div className="spinner-border text-primary" style={{width: '3rem', height: '3rem'}} role="status">
                        <span className="sr-only">Loading...</span>
                    </div>
                </div>
            )}
            <div className="container-fluid py-5 login-container">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-lg-6">
                            <div className="bg-light rounded p-4 p-sm-5">
                                <div className="text-center mb-4">
                                    <h1 className="mb-3">Login</h1>
                                </div>
                                {error && (
                                    <div className="alert alert-danger alert-dismissible fade show" role="alert">
                                        {error}
                                        <button type="button" className="btn-close" onClick={() => dispatch(setError(null))} aria-label="Close"></button>
                                    </div>
                                )}
                                {resetSuccessMessage && (
                                    <div className="alert alert-success alert-dismissible fade show" role="alert">
                                        {resetSuccessMessage}
                                        <button type="button" className="btn-close" onClick={() => setResetSuccessMessage('')} aria-label="Close"></button>
                                    </div>
                                )}
                                <form onSubmit={handleSubmit}>
                                    {/* User Type Dropdown */}
                                    <div className="form-group mb-4">
                                        <label className="form-label">Login As</label>
                                        <select
                                            className="form-select py-3 border-primary"
                                            value={userType}
                                            onChange={(e) => {
                                                setUserType(e.target.value);
                                                setShowOTP(false);
                                                setShowOTPBtn(true);
                                                setOtpCountdown(false);
                                                setCountdownSeconds(60);
                                            }}
                                        >
                                            {userTypes.map((type) => (
                                                <option key={type.value} value={type.value}>
                                                    {type.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {isClinicOrHospital ? (
                                        <>
                                            <div className="form-group mb-4">
                                                <label className="form-label">Email</label>
                                                <input
                                                    type="email"
                                                    className="form-control py-3 border-primary"
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    required
                                                    placeholder="Clinic email"
                                                />
                                            </div>
                                            <div className="form-group mb-4">
                                                <label className="form-label">Password</label>
                                                <input
                                                    type="password"
                                                    className="form-control py-3 border-primary"
                                                    name="password"
                                                    value={formData.password}
                                                    onChange={handleChange}
                                                    required
                                                    placeholder="Password"
                                                />
                                                <div className="text-end mt-2 d-none" style={{display:'none'}}>
                                                    <button
                                                        type="button"
                                                        className="btn btn-link p-0"
                                                        onClick={openForgotPassword}
                                                    >
                                                        Forgot password?
                                                    </button>
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="form-group mb-4">
                                            <label className="form-label">Mobile Number</label>
                                            <div className="row justify-content-center">
                                                <div className="col-4">
                                                    <select
                                                        className="form-select py-3 border-primary"
                                                        style={{ maxWidth: '150px' }}
                                                        value={countryCode}
                                                        onChange={(e) => setCountryCode(e.target.value)}
                                                    >
                                                        {countryCodes.map((country) => (
                                                            <option key={country.code} value={country.code}>
                                                                {country.code}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="col-8">
                                                    <input
                                                        type="tel"
                                                        className="form-control py-3 border-primary"
                                                        name="mobile"
                                                        value={formData.mobile}
                                                        onChange={handleChange}
                                                        required
                                                        placeholder="Mobile number"
                                                        pattern="[0-9]{10}"
                                                    />
                                                </div>
                                                <div className="col-12 mt-3 text-end">
                                                    <button 
                                                        className="btn btn-primary" 
                                                        type="button"
                                                        onClick={handleSendOTP}
                                                        disabled={!showOTPBtn}
                                                    >
                                                        {otpCountdown ? `Resend OTP in ${cooldownSeconds}s` : (showOTPBtn ? 'Send OTP' : 'OTP Sent')}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {!isClinicOrHospital && showOTP && (
                                        <div className="form-group mb-4">
                                            <label className="form-label">Enter OTP</label>
                                            <input
                                                type="text"
                                                className="form-control py-3 border-primary"
                                                name="otp"
                                                value={formData.otp}
                                                onChange={handleChange}
                                                required
                                                placeholder="Enter OTP"
                                                pattern="[0-9]{6}"
                                                maxLength="6"
                                            />
                                        </div>
                                    )}

                                    <button 
                                        type="submit" 
                                        className="btn btn-primary py-3 w-100"
                                        disabled={!isClinicOrHospital && !showOTP}
                                    >
                                        Login
                                    </button>
                                    {isClinicOrHospital && (
                                        <div className="text-center mt-3">
                                            <button
                                                type="button"
                                                className="btn btn-link"
                                                onClick={() => setShowClinicRegister(true)}
                                            >
                                                Register a {userType}
                                            </button>
                                        </div>
                                    )}
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {showForgotPassword && (
                <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered" role="document">
                        <div className="modal-content">
                            <form onSubmit={forgotStep === 'email' ? handleForgotPassword : handleResetPassword}>
                                <div className="modal-header">
                                    <h5 className="modal-title">Reset {userType === 'hospital' ? 'Hospital' : 'Clinic'} Password</h5>
                                    <button type="button" className="btn-close" onClick={closeForgotPassword} aria-label="Close"></button>
                                </div>
                                <div className="modal-body">
                                    {forgotMessage && (
                                        <div className="alert alert-success" role="alert">
                                            {forgotMessage}
                                        </div>
                                    )}
                                    <div className="form-group mb-4">
                                        <label className="form-label">{userType === 'hospital' ? 'Hospital Email' : 'Clinic Email'}</label>
                                        <input
                                            type="email"
                                            className="form-control py-3 border-primary"
                                            name="email"
                                            value={forgotData.email}
                                            onChange={handleForgotChange}
                                            required
                                            disabled={forgotStep === 'reset'}
                                            placeholder={userType === 'hospital' ? 'Hospital email' : 'Clinic email'}
                                        />
                                    </div>
                                    {forgotStep === 'reset' && (
                                        <>
                                            <div className="form-group mb-4">
                                                <label className="form-label">Email OTP</label>
                                                <input
                                                    type="text"
                                                    className="form-control py-3 border-primary"
                                                    name="otp"
                                                    value={forgotData.otp}
                                                    onChange={handleForgotChange}
                                                    required
                                                    placeholder="Enter 6 digit OTP"
                                                    pattern="[0-9]{6}"
                                                    maxLength="6"
                                                />
                                            </div>
                                            <div className="form-group mb-4">
                                                <label className="form-label">New Password</label>
                                                <input
                                                    type="password"
                                                    className="form-control py-3 border-primary"
                                                    name="password"
                                                    value={forgotData.password}
                                                    onChange={handleForgotChange}
                                                    required
                                                    minLength="8"
                                                    placeholder="New password"
                                                />
                                            </div>
                                            <div className="form-group mb-0">
                                                <label className="form-label">Confirm New Password</label>
                                                <input
                                                    type="password"
                                                    className="form-control py-3 border-primary"
                                                    name="password_confirmation"
                                                    value={forgotData.password_confirmation}
                                                    onChange={handleForgotChange}
                                                    required
                                                    minLength="8"
                                                    placeholder="Confirm new password"
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    {forgotStep === 'reset' && (
                                        <button type="button" className="btn btn-outline-primary me-auto" onClick={handleForgotPassword} disabled={loading}>
                                            Resend OTP
                                        </button>
                                    )}
                                    <button type="button" className="btn btn-outline-secondary" onClick={closeForgotPassword}>Cancel</button>
                                    <button type="submit" className="btn btn-primary" disabled={loading}>
                                        {forgotStep === 'email' ? 'Send OTP' : 'Reset Password'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
            {showClinicRegister && (
                <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
                        <div className="modal-content">
                            <form onSubmit={handleClinicRegister}>
                                <div className="modal-header">
                                    <h5 className="modal-title">Register {userType === 'hospital' ? 'Hospital' : 'Clinic'}</h5>
                                    <button type="button" className="btn-close" onClick={() => setShowClinicRegister(false)} aria-label="Close"></button>
                                </div>
                                <div className="modal-body">
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <label className="form-label">{userType === 'hospital' ? 'Hospital Name' : 'Clinic Name'}</label>
                                            <input type="text" className="form-control py-3 border-primary" name="clinic_name" value={registerData.clinic_name} onChange={handleRegisterChange} required />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Owner Name</label>
                                            <div className="input-group">
                                                <select className="form-select py-3 border-primary" name="owner_name_prefix" value={registerData.owner_name_prefix} onChange={handleRegisterChange} style={{ maxWidth: '120px' }}>
                                                    <option value="">Choose...</option>
                                                    <option value="Dr.">Dr.</option>
                                                    <option value="Prof.">Prof.</option>
                                                    <option value="Mr.">Mr.</option>
                                                    <option value="Mrs.">Mrs.</option>
                                                    <option value="Ms.">Ms.</option>
                                                </select>
                                                <input type="text" className="form-control py-3 border-primary" name="owner_name" value={registerData.owner_name} onChange={handleRegisterChange} required />
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Email</label>
                                            <input type="email" className="form-control py-3 border-primary" name="email" value={registerData.email} onChange={handleRegisterChange} required />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Contact Number</label>
                                            <div className="input-group">
                                                <select className="form-select py-3 border-primary" name="contact_number_code" value={registerData.contact_number_code} onChange={handleRegisterChange} style={{ maxWidth: '120px' }}>
                                                    {countryCodes.map((country) => (
                                                        <option key={country.code} value={country.code}>{country.code}</option>
                                                    ))}
                                                </select>
                                                <input type="tel" className="form-control py-3 border-primary" name="contact_number" value={registerData.contact_number} onChange={handleRegisterChange} pattern="[0-9]{10}" />
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Password</label>
                                            <input type="password" className="form-control py-3 border-primary" name="password" value={registerData.password} onChange={handleRegisterChange} required minLength="8" />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Confirm Password</label>
                                            <input type="password" className="form-control py-3 border-primary" name="password_confirmation" value={registerData.password_confirmation} onChange={handleRegisterChange} required minLength="8" />
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-outline-secondary" onClick={() => setShowClinicRegister(false)}>Cancel</button>
                                    <button type="submit" className="btn btn-primary" disabled={loading}>Register</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
